<template>
  <SlideIn
    :open="isOpen"
    :heading="`In Your Bag (${lineItemsCount})`"
    @close="isOpen = false"
  >
    <template #content>
      <CartWarnings />

      <template v-if="lines.length > 0">
        <FreeShippingProgress :subtotal="subtotal" />

        <ul>
          <li v-for="line in lines" :key="line.id">
            <CartLine
              :line="line"
              class="cart-line"
              @removeLine="handleRemoveCartLine(line)"
              @updateLine="(val) => handleUpdateCartLine(line, val)"
            />
          </li>
        </ul>

        <div class="bag-checkout">
          <div class="cart-price">
            <span class="cart-price__heading">Subtotal</span>
            <span class="cart-price__value">
              {{ $filters.toCurrency(subtotal.amount, subtotal.currencyCode) }}
            </span>
          </div>
          <Button
            tag="a"
            :href="checkoutUrl"
            name="Checkout"
            full-width
            @click="handleCheckoutClick"
          >
            Go to checkout
          </Button>
        </div>

        <NostoProductRecommendationsGrid
          class="bag-recommendations"
          :recommendations="nostoProductRecommendationsProduct"
          :placement="nostoProductRecommendationsProductId"
          overflow
          compact
          centered-heading
          product-card-variant="bag"
          heading="You May Also Like"
        />

        <BagReassurances :price="subtotal" />
      </template>

      <EmptyBagState v-else />
    </template>
  </SlideIn>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import Button from "@/components/Button.vue";
import CartLine from "@/components/cart/CartLine.vue";
import NostoProductRecommendationsGrid from "@/components/NostoProductRecommendationsGrid.vue";
import SlideIn from "@/components/SlideIn.vue";
import FreeShippingProgress from "@/components/cart/FreeShippingProgress.vue";
import EmptyBagState from "@/components/cart/EmptyBagState.vue";
import CartWarnings from "@/components/cart/CartWarnings.vue";
import BagReassurances from "@/components/cart/BagReassurances.vue";
import { markCartVisitedThisSession } from "@/helpers/cartReminder.js";

export default {
  name: "BagV2",
  components: {
    CartLine,
    SlideIn,
    Button,
    NostoProductRecommendationsGrid,
    FreeShippingProgress,
    EmptyBagState,
    CartWarnings,
    BagReassurances,
  },
  emits: ["update:open"],
  data() {
    return {
      nostoProductRecommendationsProduct: null,
      nostoProductRecommendationsProductId: null,
    };
  },
  computed: {
    ...mapState("cart", ["cart", "isCartOpen", "multipassCheckoutUrl"]),
    ...mapGetters("cart", ["lines", "subtotal", "lineItemsCount"]),
    isOpen: {
      get() {
        return this.isCartOpen;
      },
      set(val) {
        return this.setCartOpen(val);
      },
    },
    checkoutUrl() {
      if (this.multipassCheckoutUrl) {
        return this.multipassCheckoutUrl;
      }

      return this.cart.checkoutUrl;
    },
  },
  watch: {
    isOpen(val) {
      if (val) {
        this.sendNosto();
      }
    },
  },
  methods: {
    ...mapActions("cart", [
      "removeCartItem",
      "updateCartItem",
      "setCartOpen",
      "checkCartRules",
    ]),
    handleCheckoutClick() {
      markCartVisitedThisSession();
    },
    async handleRemoveCartLine({ id }) {
      try {
        await this.removeCartItem(id);
      } catch (error) {
        console.log(error);
      }

      if (this.lineItemsCount === 0) {
        this.setCartOpen(false);
      }
    },
    async handleUpdateCartLine(line, newQuantity) {
      if (newQuantity > line.quantity) {
        const cartRuleResults = await this.checkCartRules({
          productId: line.merchandise.product.id,
        });
        if (!cartRuleResults) {
          return;
        }
      }

      try {
        await this.updateCartItem({
          id: line.id,
          quantity: newQuantity,
          merchandiseId: line.merchandise.id,
        });
      } catch (error) {
        console.log(error);
      }
    },
    sendNosto() {
      // eslint-disable-next-line no-undef
      nostojs((api) => {
        api
          .defaultSession()
          .viewCart()
          .setPlacements(["cartpage-nosto-1"])
          .load()
          .then((data) => {
            this.nostoProductRecommendationsProduct =
              data.recommendations["cartpage-nosto-1"]?.products || null;
            this.nostoProductRecommendationsProductId =
              data.recommendations["cartpage-nosto-1"]?.result_id || null;
          });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.cart-line {
  border-bottom: 1px solid rgba($color-grey-dark, 0.2);
  margin: 0 -20px;
  padding: 20px;

  &:last-child {
    border-bottom: none;
  }
}

.bag-checkout {
  border-top: 1px solid #e4e4e4;
  margin: 0 -20px;
  padding: 20px 20px 0;

  :deep(.button) {
    font-family: $utility-font-family;
    font-weight: 400;
    letter-spacing: 1px;
  }
}

.cart-price {
  display: flex;
  justify-content: space-between;
  @include utility-label(15px);
  margin-bottom: 16px;
}

.bag-recommendations {
  border-top: 1px solid #e4e4e4;
  margin: 20px -20px 0;
  padding: 20px 20px 0;

  :deep(.product-recommendations__grid--overflow) {
    margin: 0 -20px;
    padding: 0 20px 16px;
  }
}
</style>
